import React, { useState, useEffect, useRef } from 'react';
import { animate, stagger } from 'animejs';
import supabase from '../supabase/supabase';
import SafeIcon from '../common/SafeIcon';
import { formatPHP, formatDate } from '../utils/format';
import * as FiIcons from 'react-icons/fi';
import EditTopupModal from '../components/topup/EditTopupModal';
import DeleteTopupModal from '../components/topup/DeleteTopupModal';

const { FiPlusCircle, FiDollarSign, FiCalendar, FiFileText, FiClock, FiEdit2, FiTrash2 } = FiIcons;
const TABLE_NAME = 'topups_1739880000000';

const Topup = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [topups, setTopups] = useState([]);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [editingTopup, setEditingTopup] = useState(null);
  const [deletingTopup, setDeletingTopup] = useState(null);
  const listRef = useRef(null);
  const msgRef = useRef(null);

  const [formData, setFormData] = useState({
    amount: '', date: new Date().toISOString().split('T')[0], note: ''
  });

  useEffect(() => { fetchTopupHistory(); }, []);

  useEffect(() => {
    if (!loadingHistory && listRef.current) {
      animate(listRef.current.querySelectorAll('.topup-row'), {
        opacity: [0, 1],
        translateX: [20, 0],
        delay: stagger(60),
        duration: 400,
        ease: 'outExpo',
      });
    }
  }, [loadingHistory, topups]);

  useEffect(() => {
    if (message.text && msgRef.current) {
      animate(msgRef.current, {
        opacity: [0, 1],
        translateY: [-10, 0],
        duration: 300,
        ease: 'outExpo',
      });
    }
  }, [message.text]);

  const fetchTopupHistory = async () => {
    try {
      setLoadingHistory(true);
      const { data, error } = await supabase.from(TABLE_NAME).select('*')
        .order('date', { ascending: false }).order('created_at', { ascending: false }).limit(20);
      if (error) throw error;
      setTopups(data || []);
    } catch (error) {
      console.error('Error fetching top-ups:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage({ type: '', text: '' });
    try {
      const { error } = await supabase.from(TABLE_NAME).insert([{
        amount: parseFloat(formData.amount), date: formData.date, note: formData.note
      }]);
      if (error) throw error;
      setMessage({ type: 'success', text: 'Funds added successfully!' });
      setFormData({ amount: '', date: new Date().toISOString().split('T')[0], note: '' });
      fetchTopupHistory();
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-10">
      <header>
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Top Up Balance</h1>
        <p className="text-gray-500 mt-1">Add funds to increase your weekly starting balance.</p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 md:p-8 h-fit">
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-10 h-10 bg-blue-50 text-blue-600 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiPlusCircle} className="text-xl" />
            </div>
            <h2 className="text-lg font-bold text-gray-900">Add New Funds</h2>
          </div>

          {message.text && (
            <div ref={msgRef} className={`mb-6 p-4 rounded-xl text-sm opacity-0 ${message.type === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
              {message.text}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Amount to Add (₱)</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <SafeIcon icon={FiDollarSign} className="text-gray-400" />
                </div>
                <input type="number" step="0.01" min="1" required
                  className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-xl focus:ring-blue-500 text-lg"
                  placeholder="0.00" value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })} />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <SafeIcon icon={FiCalendar} className="text-gray-400" />
                </div>
                <input type="date" required
                  className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-xl focus:ring-blue-500"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })} />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Note (Optional)</label>
              <div className="relative">
                <div className="absolute top-3 left-3 pointer-events-none">
                  <SafeIcon icon={FiFileText} className="text-gray-400" />
                </div>
                <textarea rows="2"
                  className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-xl focus:ring-blue-500"
                  placeholder="e.g. Salary, Bonus" value={formData.note}
                  onChange={(e) => setFormData({ ...formData, note: e.target.value })}></textarea>
              </div>
            </div>
            <button type="submit" disabled={isSubmitting}
              className="w-full flex justify-center items-center py-3.5 px-4 rounded-xl shadow-sm text-base font-bold text-white bg-blue-600 hover:bg-blue-700 transition-colors disabled:opacity-70 mt-2">
              {isSubmitting ? 'Processing...' : 'Add Funds'}
            </button>
          </form>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden flex flex-col">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-lg font-bold text-gray-900 flex items-center">
              <SafeIcon icon={FiClock} className="mr-2 text-blue-600" />Recent Top-ups
            </h2>
          </div>
          <div className="flex-1 overflow-y-auto max-h-[600px]">
            {loadingHistory ? (
              <div className="p-10 flex justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : topups.length === 0 ? (
              <div className="p-10 text-center text-gray-500"><p>No top-up history found.</p></div>
            ) : (
              <div ref={listRef} className="divide-y divide-gray-100">
                {topups.map((item) => (
                  <div key={item.id} className="topup-row opacity-0 p-5 hover:bg-gray-50 transition-colors group">
                    <div className="flex justify-between items-start">
                      <div>
                        <span className="text-xs font-bold text-gray-400 uppercase tracking-tighter">{formatDate(item.date)}</span>
                        <div className="text-lg font-bold text-blue-600 mt-0.5">{formatPHP(item.amount)}</div>
                      </div>
                      <div className="flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => setEditingTopup(item)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg">
                          <SafeIcon icon={FiEdit2} className="text-sm" />
                        </button>
                        <button onClick={() => setDeletingTopup(item)} className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg">
                          <SafeIcon icon={FiTrash2} className="text-sm" />
                        </button>
                      </div>
                    </div>
                    {item.note && <p className="text-xs text-gray-600 bg-gray-50 p-2 rounded-lg mt-2 border border-gray-100">{item.note}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {editingTopup && <EditTopupModal topup={editingTopup} onClose={() => setEditingTopup(null)} onSuccess={() => { setEditingTopup(null); fetchTopupHistory(); }} />}
      {deletingTopup && <DeleteTopupModal topup={deletingTopup} onClose={() => setDeletingTopup(null)} onSuccess={() => { setDeletingTopup(null); fetchTopupHistory(); }} />}
    </div>
  );
};

export default Topup;
